import { ratesInterface } from '../interfaces';

/**
 * Pure currency-conversion helpers — no React/Redux imports so they are safe
 * to use from reducers, plain utilities, and unit tests.
 *
 * Currency identifiers arrive in two formats at API boundaries (verified
 * against real staging responses, 2026-06):
 *  - flights:  price.currency_sign        → a sign ('$', '€', '₪')
 *  - hotels:   lowest_room_rate.currency  → also a sign, despite the name
 *  - events:   ticket.currency            → an ISO-ish code ('USD', 'EUR', 'ILS')
 * Normalize everything to the canonical sign before comparing/converting.
 */
const CURRENCY_SIGN_BY_ALIAS: Record<string, string> = {
  $: '$',
  usd: '$',
  '€': '€',
  eur: '€',
  euro: '€',
  '₪': '₪',
  ils: '₪',
  nis: '₪',
  shekel: '₪',
};

export const USD_SIGN = '$';

export const normalizeCurrencySign = (
  curr?: string | null
): string | undefined => {
  if (!curr || typeof curr !== 'string') {
    return undefined;
  }
  return CURRENCY_SIGN_BY_ALIAS[curr.trim().toLowerCase()];
};

export const hasSameCurrency = (curr1: string, curr2: string) => {
  const sign1 = normalizeCurrencySign(curr1);
  const sign2 = normalizeCurrencySign(curr2);
  if (sign1 && sign2) {
    return sign1 === sign2;
  }
  return curr1 === curr2;
};

/**
 * siteConfig.rates expresses ILS per one unit of currency: { '$': 3.26, '€': 3.77 }.
 * ILS itself is therefore 1. Returns undefined when no rate is known.
 */
const getIlsRate = (
  sign: string,
  rates: ratesInterface
): number | undefined => {
  if (sign === '₪') {
    return 1;
  }
  const rate = Number(rates?.[sign]);
  return Number.isFinite(rate) && rate > 0 ? rate : undefined;
};

export const convertEurToUsd = ({
  amount,
  rates,
}: {
  amount: number;
  rates: ratesInterface;
}) => {
  if (!rates) {
    return amount;
  }
  if (
    Number.isFinite(Number(rates['$'])) &&
    Number.isFinite(Number(rates['€']))
  ) {
    // through the shekel rates we convert from eur to usd
    return (amount * rates['€']) / rates['$'];
  }
  return NaN;
};

export const convertUsdToEur = ({
  amount,
  rates,
}: {
  amount: number;
  rates: ratesInterface;
}) => {
  if (!rates) {
    return amount;
  }
  if (
    Number.isFinite(Number(rates['$'])) &&
    Number.isFinite(Number(rates['€']))
  ) {
    return (amount * rates['$']) / rates['€'];
  }
  return amount;
};

/**
 * Convert an amount between any two currencies supported by siteConfig.rates
 * (USD/EUR/ILS triangle). Full precision — rounding is the caller's concern.
 * Unknown currency or missing rate → returns the amount unconverted and warns,
 * so behavior degrades to the legacy raw-sum instead of producing NaN.
 */
export const convertCurrencyUtil = ({
  amount,
  from,
  to,
  rates,
}: {
  amount: number;
  from?: string | null;
  to?: string | null;
  rates: ratesInterface;
}): number => {
  if (!Number.isFinite(amount)) {
    return amount;
  }
  const fromSign = normalizeCurrencySign(from);
  const toSign = normalizeCurrencySign(to);
  if (!fromSign || !toSign || fromSign === toSign) {
    if (from && to && fromSign !== toSign && (!fromSign || !toSign)) {
      console.warn(
        `convertCurrencyUtil: unknown currency "${from}" -> "${to}", amount left unconverted`
      );
    }
    return amount;
  }
  const fromRate = getIlsRate(fromSign, rates);
  const toRate = getIlsRate(toSign, rates);
  if (!fromRate || !toRate) {
    console.warn(
      `convertCurrencyUtil: missing rate for "${from}" -> "${to}", amount left unconverted`
    );
    return amount;
  }
  return (amount * fromRate) / toRate;
};

/**
 * Sum two prices that may be in different currencies.
 * Same currency → plain sum in that currency.
 * Mixed currencies → BOTH sides converted to USD and summed (the canonical
 * "mixed dynamic package → USD" rule), so the result currency is '$'.
 * Unknown currency / missing rate → that side is added unconverted (legacy
 * behavior) with a console.warn from convertCurrencyUtil.
 */
export const calcTwoPricesUtil = ({
  price1,
  curr1,
  price2,
  curr2,
  rates,
}: {
  price1: number;
  curr1: string;
  price2: number;
  curr2: string;
  rates: ratesInterface;
}): number => {
  if (!rates || hasSameCurrency(curr1, curr2)) {
    return Math.ceil(price1 + price2);
  }
  return Math.ceil(
    convertCurrencyUtil({ amount: price1, from: curr1, to: USD_SIGN, rates }) +
      convertCurrencyUtil({ amount: price2, from: curr2, to: USD_SIGN, rates })
  );
};

export const convertToTargetCurrencyUtil = ({
  price,
  curr,
  targetCurrency,
  rates,
}: {
  price: number;
  curr: string;
  targetCurrency: string;
  rates: ratesInterface;
}): number => {
  return Math.ceil(
    convertCurrencyUtil({
      amount: price,
      from: curr,
      to: targetCurrency,
      rates,
    })
  );
};

/**
 * The currency sign a combined total will be expressed in, given its parts'
 * currencies: all known and identical → that sign; anything mixed/unknown →
 * USD (the canonical "mixed dynamic package → USD" rule).
 */
export const resolveTotalCurrencySign = (
  currencies: (string | null | undefined)[]
): string => {
  const signs = currencies.map((curr) => normalizeCurrencySign(curr));
  if (
    signs.length > 0 &&
    signs.every((sign): sign is string => !!sign && sign === signs[0])
  ) {
    return signs[0];
  }
  return USD_SIGN;
};

export interface DpPricePart {
  amount?: number | null;
  currency?: string | null;
}

export interface DpTotalResult {
  /** Whole-unit total (single Math.ceil applied at the end). */
  total: number;
  /** Canonical sign the total is expressed in ('$', '€' or '₪'). */
  currency: string;
}

/**
 * Single source of truth for a dynamic package total (flight + room + optional
 * event) so search results, the product page and the shopping cart all show
 * the same number.
 *
 * Rules (agreed 2026-06):
 *  - all parts in the same currency  → sum stays in that currency
 *  - mixed currencies               → every part converted to USD
 *  - unknown currency / no rate     → part added unconverted (+ console.warn)
 *  - full-precision math, ONE Math.ceil at the very end
 */
export const calcDpTotalUtil = ({
  parts,
  rates,
  targetCurrency,
}: {
  parts: (DpPricePart | undefined | null)[];
  rates: ratesInterface;
  /** Force the result into this sign instead of deriving it from the parts.
   *  Used when the total must be expressed in a currency the caller already
   *  owns — e.g. attach mode, where the order's base currency is fixed and the
   *  mixed-parts USD fallback would show the agent a currency the server will
   *  never store the order in. */
  targetCurrency?: string;
}): DpTotalResult => {
  const validParts = parts.filter(
    (part): part is DpPricePart =>
      !!part && Number.isFinite(Number(part.amount))
  );

  const currency =
    normalizeCurrencySign(targetCurrency) ??
    resolveTotalCurrencySign(validParts.map((part) => part.currency));

  const total = validParts.reduce(
    (sum, part) =>
      sum +
      convertCurrencyUtil({
        amount: Number(part.amount),
        from: part.currency,
        to: currency,
        rates,
      }),
    0
  );

  return { total: Math.ceil(total), currency };
};
